/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICookieContext, IRequestContext } from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

import { ProductComparisonItem } from './base';
import { ComparisonManager } from './comparison-manager';

/**
 * Represents a product comparison manager which operates a list of products and cookies to store the list.
 */
export class ProductComparisonState extends ComparisonManager<string, ProductComparisonItem> {
    private static readonly _productComparisonItemsLocalStorageKey: string = '_msdyn365__productComparisonItems_';

    private static _instance?: ProductComparisonState;

    private readonly _requestContext: IRequestContext;

    public static instance(requestContext: IRequestContext): ProductComparisonState {
        const items = ProductComparisonState._retrieveItemsFromCookies(requestContext);

        if (!ProductComparisonState._instance) {
            ProductComparisonState._instance = new ProductComparisonState(requestContext, items);
        } else if (!ArrayExtensions.equals(ProductComparisonState._instance.items, items)) {
            ProductComparisonState._instance.setItems(items);
        }

        return ProductComparisonState._instance;
    }

    private static _getCookieStorageKey(requestContext: IRequestContext) {
        return `${ProductComparisonState._productComparisonItemsLocalStorageKey}__${requestContext.sitePath ?? ''}__${requestContext.user
            .customerAccountNumber ?? ''}`;
    }

    private static _retrieveItemsFromCookies(requestContext: IRequestContext): ProductComparisonItem[] {
        const items = ProductComparisonState._readFromCookie(
            requestContext.cookies,
            ProductComparisonState._getCookieStorageKey(requestContext)
        );
        if (!ArrayExtensions.hasElements(items)) {
            return [];
        }
        return items;
    }

    private static _readFromCookie(cookieContext: ICookieContext, cookieKey: string): ProductComparisonItem[] | undefined | null {
        if (!cookieKey) {
            return undefined;
        }

        if (!cookieContext.isConsentGiven()) {
            return undefined;
        }

        const cookieValue = cookieContext.get<ProductComparisonItem[] | undefined | null>(cookieKey);

        return cookieValue.value;
    }

    private static _writeToCookie(cookieContext: ICookieContext, cookieKey: string, value: ProductComparisonItem[]): void {
        if (!cookieKey) {
            return;
        }

        cookieContext.set(cookieKey, value, { secure: true, maxAge: 5_184_000 });
    }

    private constructor(requestContext: IRequestContext, items: ProductComparisonItem[]) {
        super(...items);

        this._requestContext = requestContext;

        this.listModifiedEvent.subscribe({
            /**
             * Unique identifier of the subscriber. Used to identify the subscriber among the other subscribers for the same event.
             */
            instanceId: 'ProductComparisonState instance',

            /**
             * Method which should be called once the event is triggered.
             */
            handler: () => {
                ProductComparisonState._writeToCookie(
                    this._requestContext.cookies,
                    ProductComparisonState._getCookieStorageKey(requestContext),
                    this.items
                );
            }
        });
    }
}
